import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
    BarChart3, Database, Download, Globe, Loader2, Lock, Map as MapIcon,
    RefreshCw, Search as SearchIcon, Filter, X, ChevronDown,
} from 'lucide-react';
import SearchResultsTable from '@/components/features/search/SearchResultsTable';
import SearchResultModal from '@/components/features/search/SearchResultModal';
import Pagination from '@/components/common/ui/Pagination';
import Switch from '@/components/common/ui/Switch';
import { SortDirection } from '@/components/common/ui/Table';
import { useToast } from '@/hooks/components/common/toast/useToast';
import { useFeatureAccess } from '@/contexts/FeatureAccessContext';
import { 
    useSaveSearchResultsMutation, 
    useSearchThreatIntelMutation, 
    useGetMapDataQuery,
    useLazyGetMapFilterQuery // Added the new hook
} from '@/lib/services/searchApi';
import type {
    IntelSearchRequest,
    IntelSearchResponse,
    QuickFilterType,
    SearchTypeType,
    SeverityType
} from '@/lib/api/types';
import SearchThreatMap from "@/components/features/search/SearchThreatMap";

// --- ERROR HANDLING ---
const extractErrorMessage = (error: any): { title: string; message: string } => {
    if (error?.data) {
        const errorData = error.data;
        if (errorData.success === false && errorData.error) {
            return { title: getErrorTitle(errorData), message: errorData.details || errorData.error };
        }
        if (errorData.message) return { title: 'Search Failed', message: errorData.message };
        if (typeof errorData === 'string') return { title: 'Search Failed', message: errorData };
    }
    if (error?.status) {
        switch (error.status) {
            case 400: return { title: 'Invalid Request', message: error?.data?.error || error?.data?.message || 'Please check your search parameters and try again.' };
            case 401: return { title: 'Authentication Failed', message: error?.data?.error || 'Please log in and try again.' };
            case 403: return { title: 'Access Denied', message: error?.data?.error || error?.data?.details || 'You do not have permission to perform this search.' };
            case 404: return { title: 'Not Found', message: error?.data?.error || 'The requested resource was not found.' };
            case 429: return { title: 'Rate Limit Exceeded', message: error?.data?.error || error?.data?.details || 'Too many requests. Please wait and try again.' };
            case 500: return { title: 'Server Error', message: error?.data?.error || 'An internal server error occurred. Please try again later.' };
            default: return { title: 'Search Failed', message: error?.data?.error || error?.data?.message || `Request failed with status ${error.status}` };
        }
    }
    if (error?.message) {
        if (error.message.includes('fetch')) return { title: 'Network Error', message: 'Unable to connect to the server. Please check your internet connection.' };
        return { title: 'Search Failed', message: error.message };
    }
    return { title: 'Search Failed', message: 'An unexpected error occurred. Please try again.' };
};

const getErrorTitle = (errorData: any): string => {
    const errorMessage = errorData.error?.toLowerCase() || '';
    if (errorMessage.includes('domain') && errorMessage.includes('not verified')) return 'Domain Verification Required';
    if (errorMessage.includes('access denied') || errorMessage.includes('denied')) return 'Access Denied';
    if (errorMessage.includes('quota') || errorMessage.includes('limit')) return 'Quota Exceeded';
    if (errorMessage.includes('license') || errorMessage.includes('subscription')) return 'License Issue';
    if (errorMessage.includes('permission')) return 'Permission Error';
    if (errorMessage.includes('upgrade')) return 'Upgrade Required';
    return 'Search Failed';
};

interface SearchResult {
    data_type?: string | null;
    url?: string | null;
    log_id?: number | null;
    username?: string | null;
    password?: string | null;
    log_date?: string | null;
    hostname?: string | null;
    ip?: string | null;
    malware_path?: string | null;
    anti_virus?: string | null;
    _source_endpoint?: string | null;
    country_code?: string | null;
    country?: string | null;
    priority?: 'critical' | 'high' | 'medium' | 'low';
    originalIndex: number;
}

interface SearchFilters {
    quick_filter: QuickFilterType;
    type: SearchTypeType;
    limit: number;
    include_system_info: boolean;
}

interface SearchState {
    allResults: SearchResult[];
    currentApiPage: number;
    hasMorePages: boolean;
    isLoadingMore: boolean;
    searchQuery: string;
    searchFilters: SearchFilters;
    totalPossibleResults: number;
}

type SortField = 'priority' | 'data_type' | 'url' | 'username' | 'log_date' | 'ip' | 'location';

const defaultFilters: SearchFilters = {
    quick_filter: 'domain',
    type: 'all',
    limit: 2000, 
    include_system_info: true
};

const quickFilterOptions = [
    { value: 'domain', label: 'Domain' },
    { value: 'email', label: 'Private Email Address' },
    { value: 'corporate', label: 'Corporate User Records' },
    { value: 'thirdparty', label: 'End-User Records' },
    { value: 'ip', label: 'IP Address Records' }
];

const searchTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'stealer', label: 'Stealers' },
    { value: 'combolist', label: 'Combo Lists' },
];

const Search: React.FC = () => {
    const [searchInput, setSearchInput] = useState('');
    const [quickFilter, setQuickFilter] = useState<QuickFilterType>('domain');
    const [searchType, setSearchType] = useState<SearchTypeType>('all');
    const [resolveIps] = useState(true);

    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isQuickFilterOpen, setIsQuickFilterOpen] = useState(false);
    const [isTypeFilterOpen, setIsTypeFilterOpen] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const quickFilterRef = useRef<HTMLDivElement>(null);
    const typeFilterRef = useRef<HTMLDivElement>(null);

    const [searchResults, setSearchResults] = useState<IntelSearchResponse | null>(null);
    const [searchState, setSearchState] = useState<SearchState>({
        allResults: [], currentApiPage: 1, hasMorePages: false, isLoadingMore: false,
        searchQuery: '', searchFilters: defaultFilters, totalPossibleResults: 0
    });

    const searchStateRef = useRef(searchState);
    useEffect(() => {
        searchStateRef.current = searchState;
    }, [searchState]);

    const [hasSearched, setHasSearched] = useState(false);
    const [selectedThreat, setSelectedThreat] = useState<SearchResult | null>(null);
    const [selectedThreatIndex, setSelectedThreatIndex] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sortField, setSortField] = useState<SortField>('log_date');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [currentFetchId, setCurrentFetchId] = useState<string | null>(null);
    const [showAdvancedStats, setShowAdvancedStats] = useState(false);
    const [showMap, setShowMap] = useState(true);
    const [selectedMapLocation, setSelectedMapLocation] = useState<any | null>(null);

    // API Pagination defaults
    const itemsPerPage = 25;
    const apiPageSize = 500;
    const [displayPage, setDisplayPage] = useState(1);

    // Server-Side Map Filter State
    const [getMapFilter, { isLoading: isMapFilterLoading }] = useLazyGetMapFilterQuery();
    const [mapFilteredResults, setMapFilteredResults] = useState<SearchResult[]>([]);
    const [mapFilterTotal, setMapFilterTotal] = useState(0);
    const [mapFilterApiPage, setMapFilterApiPage] = useState(1);

    const [searchThreatIntel, { isLoading: isSearching }] = useSearchThreatIntelMutation();
    const [saveSearchResults, { isLoading: isSaving }] = useSaveSearchResultsMutation();

    const { data: mapData } = useGetMapDataQuery(currentFetchId!, {
        skip: !currentFetchId || !resolveIps,
    });

    const toast = useToast();
    const { hasFeature, permissions } = useFeatureAccess();
    const canSaveIncidents = hasFeature('incidentSaving');

    useEffect(() => {
        const saved = localStorage.getItem('recentDomainSearches');
        if (saved) {
            try { setRecentSearches(JSON.parse(saved)); }
            catch (e) { console.error("Failed to parse recent searches"); }
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (dropdownRef.current && !dropdownRef.current.contains(target)) setIsDropdownOpen(false);
            if (quickFilterRef.current && !quickFilterRef.current.contains(target)) setIsQuickFilterOpen(false);
            if (typeFilterRef.current && !typeFilterRef.current.contains(target)) setIsTypeFilterOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const saveToRecentSearches = (domain: string) => {
        setRecentSearches(prev => {
            const filtered = prev.filter(d => d.toLowerCase() !== domain.toLowerCase());
            const updated = [domain, ...filtered].slice(0, 5);
            localStorage.setItem('recentDomainSearches', JSON.stringify(updated));
            return updated;
        });
    };

    const parseResults = (resultsPreview: any[], startIndex: number = 0): SearchResult[] => {
        if (!Array.isArray(resultsPreview)) return [];
        return resultsPreview
            .filter((result): result is object => result !== null && result !== undefined)
            .map((result, index) => {
                try {
                    let parsed: any;
                    if (typeof result === 'object' && result !== null) parsed = result;
                    else return null;

                    return {
                        ...parsed,
                        priority: parsed.data_type === 'stealer' ? 'high' : 'medium',
                        originalIndex: startIndex + index
                    } as SearchResult;
                } catch (error) { return null; }
            })
            .filter((result): result is SearchResult => result !== null);
    };

    const enrichedResults = useMemo(() => {
        if (!mapData?.data?.locations || searchState.allResults.length === 0) {
            return searchState.allResults;
        }

        const ipGeoMap = new Map();
        mapData.data.locations.forEach((loc: any) => {
            if (loc.ip) {
                ipGeoMap.set(loc.ip, {
                    country: loc.country,
                    countryCode: loc.countryCode || loc.country_code
                });
            }
        });

        return searchState.allResults.map(result => {
            if (result.ip && ipGeoMap.has(result.ip)) {
                const geoInfo = ipGeoMap.get(result.ip);
                return {
                    ...result,
                    country: geoInfo.country || result.country,
                    country_code: geoInfo.countryCode || result.country_code
                };
            }
            return result;
        });
    }, [searchState.allResults, mapData]);


    // --- SERVER FILTER INTEGRATION ---
    // If a map location is selected, the server handles filtering. Otherwise, use global pool.
    const activeDataPool = useMemo(() => {
        return selectedMapLocation ? mapFilteredResults : enrichedResults;
    }, [selectedMapLocation, mapFilteredResults, enrichedResults]);

    const sortedResults = useMemo(() => {
        if (!activeDataPool.length) return [];
        return [...activeDataPool].sort((a, b) => {
            if (sortField === 'location') {
                const aVal = a.country || a.country_code || '';
                const bVal = b.country || b.country_code || '';
                if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            }

            const aValue = a[sortField] || '';
            const bValue = b[sortField] || '';
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [activeDataPool, sortField, sortDirection]);

    // --- SMART PAGINATION LOGIC ---
    // Safely handles 500-sized API chunks sliced into 25-item UI pages
    const paginatedResults = useMemo(() => {
        if (selectedMapLocation) {
            const localPageIndex = (displayPage - 1) % Math.floor(apiPageSize / itemsPerPage);
            const startIndex = localPageIndex * itemsPerPage;
            return sortedResults.slice(startIndex, startIndex + itemsPerPage);
        } else {
            const startIndex = (displayPage - 1) * itemsPerPage;
            return sortedResults.slice(startIndex, startIndex + itemsPerPage);
        }
    }, [sortedResults, displayPage, itemsPerPage, selectedMapLocation]);

    const mapThreatData = useMemo(() => {
        if (enrichedResults.length === 0) return [];
        
        const counts: { [key: string]: number } = {};
        let directEntriesFound = false;
        
        enrichedResults.forEach(r => {
            const code = r.country_code || r.country;
            if (code) {
                const standardizedKey = code.toUpperCase();
                counts[standardizedKey] = (counts[standardizedKey] || 0) + 1;
                directEntriesFound = true;
            }
        });

        if (!directEntriesFound && mapData?.data?.countryCounts) {
            return Object.entries(mapData.data.countryCounts).map(([id, value]) => ({
                id: id.toUpperCase(), value: Number(value)
            }));
        }

        return Object.entries(counts).map(([id, value]) => ({
            id, value: Number(value)
        }));
    }, [enrichedResults, mapData]);

    const performSearch = async (query: string, searchFilters: SearchFilters, isLoadMore: boolean = false) => {
        if (!isLoadMore) {
            setHasSearched(true);
            setDisplayPage(1);
            setSelectedMapLocation(null);
            setMapFilteredResults([]);
            setSearchState({
                allResults: [], currentApiPage: 1, hasMorePages: false, isLoadingMore: false,
                searchQuery: query, searchFilters: searchFilters, totalPossibleResults: searchFilters.limit
            });
        } else {
            setSearchState(prev => ({ ...prev, isLoadingMore: true }));
        }

        try {
            const searchRequest: IntelSearchRequest = {
                quick_filter: searchFilters.quick_filter, query: query.trim(), type: searchFilters.type,
                limit: searchFilters.limit,
                include_system_info: searchFilters.include_system_info,
                page: isLoadMore ? searchState.currentApiPage + 1 : 1
            };

            const response = await searchThreatIntel(searchRequest).unwrap();

            if (!isLoadMore) {
                setSearchResults(response);
                setCurrentFetchId(response.fetch_id);
            }

            const newResults = parseResults(response.results_preview || [], isLoadMore ? searchState.allResults.length : 0);
            const currentTotal = isLoadMore ? searchState.allResults.length + newResults.length : newResults.length;
            
            const hasMore = newResults.length > 0 && currentTotal < searchFilters.limit;

            setSearchState(prev => ({
                ...prev,
                allResults: isLoadMore ? [...prev.allResults, ...newResults] : newResults,
                currentApiPage: isLoadMore ? prev.currentApiPage + 1 : 1,
                hasMorePages: hasMore, 
                isLoadingMore: false, 
                searchQuery: query, 
                searchFilters: searchFilters,
                totalPossibleResults: isLoadMore ? prev.totalPossibleResults : searchFilters.limit
            }));

            return { success: true, count: newResults.length, total: currentTotal };
        } catch (error) {
            const { title, message } = extractErrorMessage(error);
            if (!isLoadMore) {
                setSearchResults(null);
                setSearchState({ allResults: [], currentApiPage: 1, hasMorePages: false, isLoadingMore: false, searchQuery: '', searchFilters: defaultFilters, totalPossibleResults: 0 });
                setCurrentFetchId(null);
            } else {
                setSearchState(prev => ({ ...prev, isLoadingMore: false }));
            }
            toast({ type: 'error', title, message });
            return { success: false, count: 0, total: 0 };
        }
    };

    const executeSearch = () => {
        if (!searchInput.trim()) return;
        const currentFilters: SearchFilters = {
            quick_filter: quickFilter, type: searchType, limit: defaultFilters.limit, include_system_info: resolveIps
        };
        saveToRecentSearches(searchInput);
        performSearch(searchInput, currentFilters, false);
        setIsDropdownOpen(false);
        setIsQuickFilterOpen(false);
        setIsTypeFilterOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') executeSearch();
    };

    const loadMoreResults = async () => {
        if (!searchState.hasMorePages || searchState.isLoadingMore) return;
        await performSearch(searchState.searchQuery, searchState.searchFilters, true);
    };

    const executeBulkSyncLoop = async () => {
        let currentState = searchStateRef.current;
        let currentPage = currentState.currentApiPage + 1;
        let running = true;

        while (running && currentState.allResults.length < defaultFilters.limit) {
            const searchRequest: IntelSearchRequest = {
                quick_filter: currentState.searchFilters.quick_filter,
                query: currentState.searchQuery.trim(),
                type: currentState.searchFilters.type,
                limit: 100,
                include_system_info: currentState.searchFilters.include_system_info,
                page: currentPage
            };

            const response = await searchThreatIntel(searchRequest).unwrap();
            const newResults = parseResults(response.results_preview || [], currentState.allResults.length);

            if (newResults.length === 0) {
                running = false;
                break;
            }

            setSearchState(prev => {
                const combined = [...prev.allResults, ...newResults];
                return {
                    ...prev,
                    allResults: combined,
                    currentApiPage: currentPage,
                    hasMorePages: newResults.length > 0 && combined.length < defaultFilters.limit,
                    totalPossibleResults: combined.length
                };
            });

            currentState = {
                ...currentState,
                allResults: [...currentState.allResults, ...newResults],
                currentApiPage: currentPage,
                hasMorePages: newResults.length > 0 && [...currentState.allResults, ...newResults].length < defaultFilters.limit
            };

            currentPage++;
            await new Promise(resolve => setTimeout(resolve, 120));
        }
    };

    const autoLoadAllResults = async () => {
        if (!searchStateRef.current.hasMorePages || searchStateRef.current.isLoadingMore) return;
        setSearchState(prev => ({ ...prev, isLoadingMore: true }));
        try {
            await executeBulkSyncLoop();
            toast({ type: 'success', title: 'Data Sync Complete', message: 'All available records successfully indexed.' });
        } catch (error) {
            toast({ type: 'error', title: 'Load Interrupted', message: 'Failed to complete full dataset cache.' });
        } finally {
            setSearchState(prev => ({ ...prev, isLoadingMore: false }));
        }
    };

    const handleSaveAllResults = async () => {
        if (!canSaveIncidents) return toast({ type: 'warning', title: 'Feature Not Available', message: 'Incident saving requires an upgrade.' });
        if (!permissions.hasValidLicense) return toast({ type: 'error', title: 'License Expired', message: 'License expired.' });
        if (!currentFetchId) return toast({ type: 'error', title: 'Save Failed', message: 'No search session available.' });

        try {
            const savePayload = { fetch_id: currentFetchId, save_all: true, default_severity: 'medium' as SeverityType, default_status: 'new' as const };
            const response = await saveSearchResults(savePayload).unwrap();
            toast({ type: 'success', title: 'All Threats Saved', message: `Saved ${response.saved_count} threats.` });
        } catch (error) { toast({ type: 'error', ...extractErrorMessage(error) }); }
    };

    const handleSaveThreat = async (threat: SearchResult) => {
        if (!canSaveIncidents) return toast({ type: 'warning', title: 'Feature Not Available', message: 'Incident saving requires an upgrade.' });
        if (!currentFetchId) return toast({ type: 'error', title: 'Save Failed', message: 'No search session available.' });

        try {
            const savePayload = { fetch_id: currentFetchId, save_all: false, indices: [threat.originalIndex], default_severity: 'medium' as SeverityType, default_status: 'new' as const };
            const response = await saveSearchResults(savePayload).unwrap();
            toast({ type: 'success', title: 'Threat Saved', message: `Saved ${response.saved_count} threat.` });
        } catch (error) { toast({ type: 'error', ...extractErrorMessage(error) }); }
    };

    const exportResults = (format: 'csv' | 'json') => {
        const dataToExport = activeDataPool;
        if (!dataToExport.length) return toast({ type: 'warning', title: 'No Data', message: 'No data to export.' });

        try {
            let content, filename, mimeType;
            const filterName = selectedMapLocation?.countryCode || selectedMapLocation?.country || 'global';

            if (format === 'csv') {
                const headers = ['data_type', 'username', 'password', 'ip', 'hostname', 'url', 'log_date'];
                content = [
                    headers.join(','),
                    ...dataToExport.map(result => headers.map(header => {
                        const value = result[header as keyof SearchResult] || '';
                        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
                    }).join(','))
                ].join('\n');
                filename = `threat_results_${filterName}_${new Date().toISOString().split('T')[0]}.csv`;
                mimeType = 'text/csv';
            } else {
                content = JSON.stringify(dataToExport, null, 2);
                filename = `threat_results_${filterName}_${new Date().toISOString().split('T')[0]}.json`;
                mimeType = 'application/json';
            }

            const blob = new Blob([content], { type: mimeType });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
            toast({ type: 'success', title: 'Export Complete', message: `${dataToExport.length} results exported.` });
        } catch (error) {
            toast({ type: 'error', title: 'Export Failed', message: 'Failed to export results.' });
        }
    };

    const handleThreatClick = (threat: SearchResult) => {
        setSelectedThreat(threat);
        const index = activeDataPool.findIndex(t => t.originalIndex === threat.originalIndex);
        setSelectedThreatIndex(index);
        setIsModalOpen(true);
    };

    // --- SERVER SIDE MAP FILTERING CORE ---
    const fetchMapFilteredData = async (countryCode: string, apiPage: number) => {
        if (!currentFetchId) return;
        try {
            const response = await getMapFilter({
                fetch_id: currentFetchId,
                country_code: countryCode,
                page: apiPage,
                page_size: apiPageSize
            }).unwrap();

            const newResults = parseResults(response.results || []);
            setMapFilteredResults(newResults);
            setMapFilterTotal(response.total_count || response.total || newResults.length);
            setMapFilterApiPage(apiPage);
        } catch (error) {
            toast({ type: 'error', ...extractErrorMessage(error) });
            // Fallback clear if fetching fails entirely
            setSelectedMapLocation(null);
            setMapFilteredResults([]);
        }
    };

    const handleMapLocationSelect = async (location: any) => {
        const targetCountryCode = location.countryCode || location.country || location.name;
        const prevCountryCode = selectedMapLocation?.countryCode || selectedMapLocation?.country || selectedMapLocation?.name;
        const isDeselecting = prevCountryCode === targetCountryCode;

        if (isDeselecting) {
            setSelectedMapLocation(null);
            setDisplayPage(1);
            setMapFilteredResults([]);
            setMapFilterTotal(0);
            return;
        }

        setSelectedMapLocation(location);
        setDisplayPage(1);
        fetchMapFilteredData(targetCountryCode, 1);
    };

    const handlePageChange = (newPage: number) => {
        if (selectedMapLocation) {
            const requiredApiPage = Math.ceil((newPage * itemsPerPage) / apiPageSize);
            if (requiredApiPage !== mapFilterApiPage) {
                const countryCode = selectedMapLocation.countryCode || selectedMapLocation.country || selectedMapLocation.name;
                fetchMapFilteredData(countryCode, requiredApiPage);
            }
        }
        setDisplayPage(newPage);
    };

    const handleSort = (field: SortField, direction: SortDirection) => {
        setSortField(field); setSortDirection(direction); setDisplayPage(1);
    };

    const handleModalNavigate = (index: number) => {
        if (index >= 0 && index < activeDataPool.length) {
            setSelectedThreatIndex(index);
            setSelectedThreat(activeDataPool[index]);
        }
    };

    const totalDisplayPages = Math.ceil((selectedMapLocation ? mapFilterTotal : searchState.allResults.length) / itemsPerPage);

    const renderInlineSearchBar = () => (
        <div className="flex flex-wrap md:flex-nowrap items-center gap-3 w-full relative z-50 mb-8 print:hidden">
            <div className="relative min-w-[180px]" ref={dropdownRef}>
                <div onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="bg-[#0A0B0E] border border-[#1A1D24] text-slate-300 text-[13px] px-4 py-2.5 rounded-lg flex items-center cursor-pointer hover:border-slate-500 transition-colors w-full select-none shadow-inner">
                    <SearchIcon className="w-4 h-4 mr-2 text-[#50A970]" />
                    <span>Recent Searches</span>
                    <ChevronDown className={`w-4 h-4 ml-auto opacity-50 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </div>
                {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0A0B0E] border border-[#1A1D24] rounded-lg shadow-2xl overflow-hidden z-50">
                        {recentSearches.length === 0 ? (
                            <div className="px-4 py-3 text-xs text-slate-500 italic text-center">No recent searches</div>
                        ) : (
                            <ul className="py-1">
                                {recentSearches.map((query, idx) => (
                                    <li key={idx} onClick={() => { setSearchInput(query); setIsDropdownOpen(false); }} className="px-4 py-2.5 text-[13px] text-slate-300 hover:bg-[#161A23] hover:text-[#50A970] cursor-pointer transition-colors border-b border-[#1A1D24]/50 last:border-0">
                                        {query}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <div className="relative min-w-[140px]" ref={quickFilterRef}>
                <div onClick={() => setIsQuickFilterOpen(!isQuickFilterOpen)} className="bg-[#0A0B0E] border border-[#1A1D24] text-slate-300 text-[13px] px-4 py-2.5 rounded-lg flex items-center cursor-pointer hover:border-slate-500 transition-colors w-full select-none shadow-inner">
                    <span>{quickFilterOptions.find(o => o.value === quickFilter)?.label || 'Domain'}</span>
                    <ChevronDown className={`w-4 h-4 ml-auto opacity-50 transition-transform ${isQuickFilterOpen ? 'rotate-180' : ''}`} />
                </div>
                {isQuickFilterOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0A0B0E] border border-[#1A1D24] rounded-lg shadow-2xl overflow-hidden z-50">
                        <ul className="py-1">
                            {quickFilterOptions.map((opt) => (
                                <li key={opt.value} onClick={() => { setQuickFilter(opt.value as QuickFilterType); setIsQuickFilterOpen(false); }} className="px-4 py-2.5 text-[13px] text-slate-300 hover:bg-[#161A23] hover:text-[#50A970] cursor-pointer transition-colors border-b border-[#1A1D24]/50 last:border-0">
                                    {opt.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <div className="relative min-w-[140px]" ref={typeFilterRef}>
                <div onClick={() => setIsTypeFilterOpen(!isTypeFilterOpen)} className="bg-[#0A0B0E] border border-[#1A1D24] text-slate-300 text-[13px] px-4 py-2.5 rounded-lg flex items-center cursor-pointer hover:border-slate-500 transition-colors w-full select-none shadow-inner">
                    <span>{searchTypeOptions.find(o => o.value === searchType)?.label || 'All Types'}</span>
                    <ChevronDown className={`w-4 h-4 ml-auto opacity-50 transition-transform ${isTypeFilterOpen ? 'rotate-180' : ''}`} />
                </div>
                {isTypeFilterOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0A0B0E] border border-[#1A1D24] rounded-lg shadow-2xl overflow-hidden z-50">
                        <ul className="py-1">
                            {searchTypeOptions.map((opt) => (
                                <li key={opt.value} onClick={() => { setSearchType(opt.value as SearchTypeType); setIsTypeFilterOpen(false); }} className="px-4 py-2.5 text-[13px] text-slate-300 hover:bg-[#161A23] hover:text-[#50A970] cursor-pointer transition-colors border-b border-[#1A1D24]/50 last:border-0">
                                    {opt.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <div className="flex-1 relative">
                <input
                    type="text"
                    placeholder="Enter indicator..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full bg-[#0A0B0E] border border-[#1A1D24] text-sm text-white px-4 py-2.5 rounded-lg focus:outline-none focus:border-[#50A970] transition-colors placeholder-slate-600 font-mono shadow-inner"
                />
            </div>

            <button onClick={executeSearch} disabled={isSearching} className="bg-[#50A970] hover:bg-[#3d8556] text-black font-bold px-8 py-2.5 rounded-lg transition-colors text-[13px] disabled:opacity-50 flex items-center gap-2 uppercase tracking-wide shadow-[0_0_15px_rgba(80,169,112,0.15)]">
                {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isSearching ? 'SEARCHING...' : 'SEARCH'}
            </button>
        </div>
    );

    return (
        <div className="min-h-screen bg-black text-white font-mono p-8 space-y-6">
            <div className="flex items-end justify-between mb-4">
                <div>
                    <h1 className="text-[22px] font-bold text-white tracking-wide">Threat Intelligence Search</h1>
                    <p className="text-[10px] mt-1 font-semibold uppercase tracking-widest text-[#4da760]">System_Status: SUCCESS</p>
                </div>
                {hasSearched && (
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-[10px] text-slate-500 uppercase tracking-wider">
                            <span>Last_Sync: {new Date().toISOString().replace('T', ' ').substring(0, 19)} UTC</span>
                            <span className="w-2 h-2 rounded-full bg-[#50A970] shadow-[0_0_8px_rgba(80,169,112,0.8)] animate-pulse"></span>
                        </div>
                        <button onClick={() => setShowMap(!showMap)} className="bg-transparent border border-[#1A1D24] text-slate-300 hover:text-white hover:bg-[#161A23] font-bold px-4 py-2.5 rounded-lg transition-colors text-[12px] flex items-center gap-2">
                            {showMap ? <MapIcon className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
                            {showMap ? 'Hide Map' : 'Show Map'}
                        </button>
                    </div>
                )}
            </div>

            {renderInlineSearchBar()}

            {!hasSearched && !isSearching && (
                <div className="absolute inset-0 top-[200px] z-40 flex items-center justify-center backdrop-blur-sm bg-black/40">
                    <div className="bg-[#0A0B0E] border border-[#1A1D24] shadow-[0_0_50px_rgba(0,0,0,0.8)] rounded-2xl w-full max-w-[500px] p-8 flex flex-col relative overflow-hidden">
                        <div className="absolute -top-12 -left-12 w-48 h-48 border border-white/5 rounded-full pointer-events-none"></div>
                        <div className="absolute -top-6 -left-6 w-36 h-36 border border-white/5 rounded-full pointer-events-none"></div>
                        <div className="w-12 h-12 border border-[#1A1D24] bg-[#12151C] rounded-xl flex items-center justify-center mb-6 relative z-10 shadow-inner">
                            <SearchIcon className="w-5 h-5 text-slate-300" />
                        </div>
                        <h2 className="text-[18px] font-bold text-white mb-2 relative z-10 tracking-wide uppercase">Search Tips</h2>
                        <div className="text-[12px] text-slate-400 font-mono leading-relaxed relative z-10">
                            <ul className="list-disc list-inside">
                                <li>Use specific domains or emails for more targeted results.</li>
                                <li>Corporate Searches work best with company domain names.</li>
                                <li>IP Searches support both IPv4 and IPv6 formats.</li>
                                <li>Enable system info for detailed technical information.</li>
                                <li><b>Pagination:</b> Results load 25 at a time - use "Load More" or "Load All" for additional pages</li>
                                <li>Dynamic streaming loads extensive record sequences seamlessly on request.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {hasSearched && showMap && currentFetchId && (
                <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 shadow-lg animate-fade-in mb-4">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-[14px] font-bold text-white tracking-wide uppercase">Geospatial Distribution</h2>
                    </div>
                    <div className="w-full rounded-lg overflow-hidden border border-[#1A1D24]">
                        <SearchThreatMap fetchId={currentFetchId} data={mapThreatData} isLoading={isSearching} onLocationSelect={handleMapLocationSelect} />
                    </div>
                </div>
            )}

            {hasSearched && (
                <div className="space-y-4">
                    {selectedMapLocation && (
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-[#12151C] border border-[#50A970]/30 rounded-lg animate-fade-in shadow-sm">
                            <div className="flex items-center gap-3 text-sm text-[#50A970]">
                                {isMapFilterLoading ? <Loader2 className="w-5 h-5 flex-shrink-0 animate-spin" /> : <Filter className="w-5 h-5 flex-shrink-0" />}
                                <span className="font-mono text-[12px]">
                                    {isMapFilterLoading ? 'Fetching server records for:' : `Showing ${mapFilterTotal} results from`}
                                    <strong className="ml-2 px-2.5 py-1 bg-[#0A0B0E] border border-[#50A970]/30 rounded text-white shadow-sm uppercase tracking-wide">
                                        {selectedMapLocation.country || selectedMapLocation.countryCode || selectedMapLocation.name || 'Selected Region'}
                                    </strong>
                                </span>
                            </div>
                            <button onClick={() => { setSelectedMapLocation(null); setDisplayPage(1); setMapFilteredResults([]); setMapFilterTotal(0); }} className="mt-3 sm:mt-0 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-[11px] font-bold bg-[#0A0B0E] text-slate-400 hover:text-[#FF3333] border border-[#1A1D24] transition-colors uppercase tracking-wider">
                                Clear Filter <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <h2 className="text-[14px] font-bold text-white tracking-wide uppercase">Threat Log</h2>
                            {(searchState.allResults.length > 0 || selectedMapLocation) && (
                                <span className="text-[11px] text-slate-400 font-mono bg-[#12151C] border border-[#1A1D24] px-2 py-1 rounded tracking-wide">
                                    {selectedMapLocation ? (
                                        <>Showing <strong className="text-[#50A970]">{mapFilterTotal}</strong> results from {selectedMapLocation.country || selectedMapLocation.countryCode || selectedMapLocation.name}</>
                                    ) : (
                                        <><strong className="text-white">{activeDataPool.length}</strong> HITS REGISTERED</>
                                    )}
                                </span>
                            )}
                        </div>

                        {searchState.allResults.length > 0 && !selectedMapLocation && (
                            <div className="flex items-center gap-3">
                                {searchState.hasMorePages && (
                                    <>
                                        <button onClick={loadMoreResults} disabled={searchState.isLoadingMore} className="bg-transparent border border-[#1A1D24] text-slate-300 hover:text-white hover:bg-[#161A23] font-bold px-4 py-2 rounded-lg transition-colors text-[12px] flex items-center gap-2 disabled:opacity-50">
                                            {searchState.isLoadingMore ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Load More
                                        </button>
                                        <button onClick={autoLoadAllResults} disabled={searchState.isLoadingMore} className="bg-transparent border border-[#1A1D24] text-slate-300 hover:text-white hover:bg-[#161A23] font-bold px-4 py-2 rounded-lg transition-colors text-[12px] flex items-center gap-2 disabled:opacity-50">
                                            <Download className="h-3 w-3" /> Load All
                                        </button>
                                    </>
                                )}
                                <div className="flex items-center gap-2 px-2 border-l border-[#1A1D24] pl-4">
                                    <BarChart3 className="h-4 w-4 text-slate-500" />
                                    <Switch checked={showAdvancedStats} onCheckedChange={setShowAdvancedStats} />
                                    <span className="text-[11px] text-slate-400 font-mono uppercase tracking-wide">Metrics</span>
                                </div>
                                <button onClick={() => exportResults('csv')} className="bg-transparent border border-[#1A1D24] text-slate-300 hover:text-white hover:bg-[#161A23] font-bold px-4 py-2 rounded-lg transition-colors text-[12px] flex items-center gap-2">
                                    <Download className="h-3 w-3" /> CSV Export
                                </button>
                                {canSaveIncidents ? (
                                    <button onClick={handleSaveAllResults} disabled={isSaving || !permissions.hasValidLicense} className="bg-[#50A970] hover:bg-[#3d8556] text-black font-bold px-4 py-2 rounded-lg transition-colors text-[12px] flex items-center gap-2 disabled:opacity-50">
                                        {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Database className="h-3 w-3" />} Save To Incidents
                                    </button>
                                ) : (
                                    <button disabled className="bg-transparent border border-[#1A1D24] text-slate-500 font-bold px-4 py-2 rounded-lg text-[12px] flex items-center gap-2 opacity-50 cursor-not-allowed" title="Incident saving requires upgrade">
                                        <Lock className="h-3 w-3" /> Save To Incidents
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {showAdvancedStats && searchResults && !selectedMapLocation && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-6 bg-[#0A0B0E] rounded-xl border border-[#1A1D24] shadow-lg animate-fade-in">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-[#50A970]">{searchResults.remaining_daily_calls || 'Unlimited'}</div>
                                <div className="text-[10px] text-slate-500 font-mono tracking-widest uppercase mt-1">API Quota</div>
                            </div>
                            <div className="text-center border-l border-[#1A1D24]">
                                <div className="text-2xl font-bold text-[#50A970]">{searchResults.endpoints_called?.length || 0}</div>
                                <div className="text-[10px] text-slate-500 font-mono tracking-widest uppercase mt-1">Sources</div>
                            </div>
                            <div className="text-center border-l border-[#1A1D24]">
                                <div className="text-2xl font-bold text-slate-300">{Math.floor(searchResults.expires_in_seconds / 60)}m</div>
                                <div className="text-[10px] text-slate-500 font-mono tracking-widest uppercase mt-1">Cache TTL</div>
                            </div>
                            <div className="text-center border-l border-[#1A1D24]">
                                <div className="text-2xl font-bold text-[#FF3333]">{enrichedResults.filter(r => r.priority === 'high' || r.priority === 'critical').length}</div>
                                <div className="text-[10px] text-slate-500 font-mono tracking-widest uppercase mt-1">Critical Hits</div>
                            </div>
                        </div>
                    )}

                    {searchState.hasMorePages && !selectedMapLocation && (
                        <div className="bg-[#12151C] border border-[#50A970]/30 rounded-lg p-4 animate-fade-in">
                            <div className="flex items-start gap-3">
                                {searchState.isLoadingMore ? (
                                    <Loader2 className="h-5 w-5 text-[#50A970] flex-shrink-0 mt-0.5 animate-spin" />
                                ) : (
                                    <SearchIcon className="h-5 w-5 text-[#50A970] flex-shrink-0 mt-0.5" />
                                )}
                                <div className="space-y-1">
                                    <h4 className="text-[13px] font-bold text-[#50A970] uppercase tracking-wide">
                                        {searchState.isLoadingMore ? `Streaming Bulk Log Sequences (${searchState.allResults.length} Sync'd)` : 'Deep Data Sequences Detected'}
                                    </h4>
                                    <div className="text-[11px] text-slate-400 font-mono">
                                        {searchState.isLoadingMore
                                            ? 'Pulling downstream entries recursively from background PHP workers to map comprehensive sets locally.'
                                            : `Displaying active index frame buffer (${searchState.allResults.length} items parsed). Activate "Load All" to execute full automated script downloads.`}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeDataPool.length > 0 ? (
                        <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl overflow-hidden shadow-lg">
                            <SearchResultsTable
                                results={paginatedResults}
                                onRowClick={handleThreatClick}
                                onSort={handleSort}
                                sortField={sortField}
                                sortDirection={sortDirection}
                                canSaveIncidents={canSaveIncidents}
                                onSaveThreat={handleSaveThreat}
                                isLoading={searchState.isLoadingMore || isMapFilterLoading}
                                activeFilter={selectedMapLocation ? selectedMapLocation.country || selectedMapLocation.name : null}
                            />
                            {totalDisplayPages > 1 && (
                                <div className="flex justify-center p-4 border-t border-[#1A1D24] bg-[#12151C]">
                                    <Pagination currentPage={displayPage} totalItems={selectedMapLocation ? mapFilterTotal : activeDataPool.length} itemsPerPage={itemsPerPage} onPageChange={handlePageChange} loading={isMapFilterLoading} />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center p-12 bg-[#0A0B0E] rounded-xl border border-[#1A1D24] shadow-lg">
                            {isSearching || isMapFilterLoading ? (
                                <div className="flex flex-col items-center gap-4">
                                    <Loader2 className="h-10 w-10 text-[#50A970] animate-spin" />
                                    <h3 className="text-[14px] font-bold text-white tracking-wide uppercase">Processing...</h3>
                                    <div className="text-xs text-slate-400 font-mono">Querying deep web sources.</div>
                                </div>
                            ) : (
                                <>
                                    <SearchIcon className="h-10 w-10 text-[#1A1D24] mx-auto mb-4" />
                                    <h3 className="text-[14px] font-bold text-white tracking-wide uppercase mb-2">No Match Found</h3>
                                    <div className="text-xs text-slate-400 font-mono">Adjust parameters or clear map filters.</div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            {selectedThreat && (
                <SearchResultModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    threat={selectedThreat}
                    threats={activeDataPool}
                    currentIndex={selectedThreatIndex}
                    onNavigate={handleModalNavigate}
                    onSaveToThreats={canSaveIncidents ? handleSaveThreat : undefined}
                    fetchId={currentFetchId || undefined}
                    context="search"
                />
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #1A1D24; border-radius: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #50A970; }
            `}} />
        </div>
    );
};

export default Search;